/** Payload helpers for WhatsApp Cloud API / coexistence webhooks. */

export const COEXISTENCE_FINISH_EVENT = 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';

const EMBEDDED_SIGNUP_FINISH_EVENTS = new Set([
  'FINISH',
  'FINISH_ONLY_WABA',
  COEXISTENCE_FINISH_EVENT,
]);

/** O wizard completo manda FINISH; a coexistência manda o evento longo. */
export function isEmbeddedSignupFinish(event: unknown): boolean {
  return typeof event === 'string' && EMBEDDED_SIGNUP_FINISH_EVENTS.has(event);
}

export const WHATSAPP_CLOUD_WINDOW_MESSAGE =
  'Fora da janela de 24h do WhatsApp. O contato precisa enviar uma nova mensagem primeiro.';

const FALLBACK_WHATSAPP_WEBHOOK_VERIFY_TOKEN =
  'bfde6852897bc7178265a8bbbfd92efadcee13b595f682e8bb1da012ebef7ae7';

function readServerEnv(name: string): string | undefined {
  const runtime = globalThis as { Deno?: { env: { get(key: string): string | undefined } } };
  return runtime.Deno?.env.get(name);
}

/** Tokens que o webhook aceita. O fallback permanece: a Meta já verifica com ele. */
export function whatsappWebhookVerifyTokens(): string[] {
  return [
    readServerEnv('WHATSAPP_WEBHOOK_VERIFY_TOKEN'),
    readServerEnv('META_VERIFY_TOKEN'),
    readServerEnv('INSTAGRAM_WEBHOOK_VERIFY_TOKEN'),
    FALLBACK_WHATSAPP_WEBHOOK_VERIFY_TOKEN,
  ].filter((value): value is string => Boolean(value));
}

const DELIVERY_RANK: Record<string, number> = { sent: 1, delivered: 2, read: 3 };

const META_ID_RE = /^\d{5,32}$/;

export function isMetaNumericId(value: unknown): value is string {
  return typeof value === 'string' && META_ID_RE.test(value);
}

/** A Meta manda o id como string ou número no postMessage do Embedded Signup. */
export function metaNumericId(value: unknown): string | null {
  const text = typeof value === 'number' && Number.isSafeInteger(value)
    ? String(value)
    : value;
  return isMetaNumericId(text) ? text : null;
}

/** DELETE subscribed_apps repetido: a parceria já foi encerrada. */
export function isWabaAlreadyUnsubscribed(error: { code?: number; message?: string } | undefined): boolean {
  if (!error) return false;
  const message = (error.message || '').toLowerCase();
  return /not subscribed|already unsubscribed|n[aã]o est[aá] inscrito/.test(message);
}

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export function phoneToRemoteJid(phone: string): string {
  let digits = digitsOnly(phone);
  if (digits.startsWith('55') && digits.length === 13 && digits[4] === '9') digits = digits.slice(0, 4) + digits.slice(5);
  return `${digits}@s.whatsapp.net`;
}

export function unixToIso(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return new Date().toISOString();
  const ms = n > 1e12 ? n : n * 1000;
  return new Date(ms).toISOString();
}

const MEDIA_TYPES = new Set(['image', 'audio', 'video', 'document', 'sticker']);

export type CloudMedia = {
  id: string;
  mimeType: string;
  filename?: string;
};

export function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function mediaCaption(message: Record<string, unknown>, type: string): string {
  const block = asRecord(message[type]);
  return block && typeof block.caption === 'string' ? block.caption.trim() : '';
}

export function cloudMediaFromMessage(message: Record<string, unknown>): CloudMedia | null {
  const type = String(message.type || '');
  if (!MEDIA_TYPES.has(type)) return null;
  const block = asRecord(message[type]);
  if (!block || typeof block.id !== 'string' || !block.id) return null;
  return {
    id: block.id,
    mimeType: typeof block.mime_type === 'string' ? block.mime_type : 'application/octet-stream',
    filename: typeof block.filename === 'string' ? block.filename : undefined,
  };
}

export function describeCloudMessage(message: Record<string, unknown>): {
  content: string;
  messageType: string;
} {
  const type = String(message.type || 'text');
  if (type === 'text') {
    const text = message.text as { body?: string } | undefined;
    const body = typeof message.body === 'string' ? message.body : text?.body;
    return { content: body || '', messageType: 'text' };
  }
  if (type === 'media_placeholder') return { content: '[mídia]', messageType: 'text' };

  const labels: Record<string, { messageType: string; content: string }> = {
    image: { messageType: 'image', content: '📷 Imagem' },
    audio: { messageType: 'audio', content: '🎤 Áudio' },
    video: { messageType: 'video', content: '🎥 Vídeo' },
    document: { messageType: 'document', content: '📄 Documento' },
    sticker: { messageType: 'sticker', content: 'Figurinha' },
    location: { messageType: 'location', content: '📍 Localização' },
    contacts: { messageType: 'contact', content: '👤 Contato' },
  };
  const described = labels[type] || { content: `[${type}]`, messageType: 'text' };
  const caption = mediaCaption(message, type);
  return caption ? { ...described, content: caption } : described;
}

/** Avança sent → delivered → read. Falha substitui qualquer estado anterior. */
export function nextCloudDeliveryStatus(
  current: string | null | undefined,
  incoming: string,
): string | null {
  if (incoming === 'failed') return current === 'failed' ? null : 'failed';
  const next = DELIVERY_RANK[incoming];
  if (!next) return null;
  const prev = DELIVERY_RANK[current || ''] ?? 0;
  if (next <= prev) return null;
  return incoming;
}

/** Nota quando o app do celular recusa ou falha o sync de histórico. */
export function historySyncNote(history: unknown): string | null {
  if (!Array.isArray(history)) return null;
  for (const chunk of history) {
    const block = asRecord(chunk);
    if (!block || !Array.isArray(block.errors) || block.errors.length === 0) continue;
    for (const raw of block.errors) {
      const error = asRecord(raw);
      if (!error) continue;
      const code = Number(error.code);
      if (code === 2593109) return 'O WhatsApp Business não compartilhou o histórico.';
      const title = typeof error.title === 'string' ? error.title.trim() : '';
      const message = typeof error.message === 'string' ? error.message.trim() : '';
      if (title || message) return title || message;
    }
    return 'O WhatsApp Business não compartilhou o histórico.';
  }
  return null;
}

export type CloudInbound = {
  remotePhone: string;
  messageId: string;
  sender: 'me' | 'them';
  timestamp: string;
  content: string;
  messageType: string;
  contactName?: string;
  media?: CloudMedia;
  quotedMessageId?: string;
};

function contactNameFor(contacts: unknown, phone: string): string | undefined {
  if (!Array.isArray(contacts)) return undefined;
  const digits = digitsOnly(phone);
  for (const item of contacts) {
    const row = asRecord(item);
    if (!row) continue;
    const waId = typeof row.wa_id === 'string' ? digitsOnly(row.wa_id) : '';
    if (waId && waId !== digits) continue;
    const profile = asRecord(row.profile);
    const name = profile && typeof profile.name === 'string' ? profile.name.trim() : '';
    if (name) return name;
  }
  return undefined;
}

export function inboundFromCloudMessage(
  message: Record<string, unknown>,
  opts: { businessPhone: string; fromMe: boolean; contacts?: unknown },
): CloudInbound | null {
  const messageId = typeof message.id === 'string' ? message.id : '';
  if (!messageId) return null;

  const from = typeof message.from === 'string' ? digitsOnly(message.from) : '';
  const to = typeof message.to === 'string' ? digitsOnly(message.to) : '';
  const business = digitsOnly(opts.businessPhone);
  const remotePhone = opts.fromMe ? to : from;
  if (!remotePhone || remotePhone === business) return null;

  const described = describeCloudMessage(message);
  const media = cloudMediaFromMessage(message) || undefined;
  if (!described.content && described.messageType === 'text' && !media) return null;

  const context = asRecord(message.context);
  const quotedMessageId = context && typeof context.id === 'string' ? context.id : undefined;

  return {
    remotePhone,
    messageId,
    sender: opts.fromMe ? 'me' : 'them',
    timestamp: unixToIso(message.timestamp),
    content: described.content,
    messageType: described.messageType,
    contactName: contactNameFor(opts.contacts, remotePhone),
    media,
    quotedMessageId,
  };
}
